import React from "react";
import { Table } from "react-bootstrap";
import { InkomensanalyseNbiOverzichtJaar } from "../../../.generated/rapportage/rapportagetypes";
import { bedragFormat, mapStringToLocalDate } from "adviesbox-shared";

const calculateYear = (startDatum: string | undefined, jaar: number): string =>
  mapStringToLocalDate(startDatum)
    ?.plusYears(jaar - 1)
    .year()
    .toString() ?? "";

interface NBITableProps {
  nbi: InkomensanalyseNbiOverzichtJaar[];
  voetnoot?: string;
  startDatum?: string;
}

const bedragFormatNull = (bedrag: number | null, decimals = 0): string =>
  bedrag !== null ? bedragFormat(bedrag, decimals) : "";

export const TableNettoBesteedbaarInkomen: React.FC<NBITableProps> = ({ nbi, voetnoot, startDatum }) => {
  if (!nbi || nbi.length === 0) return <></>;

  return (
    <Table responsive striped bordered hover className="mb-2">
      <thead>
        <tr>
          <th>Jaar</th>
          <th>Leeftijd aanvrager</th>
          <th>Leeftijd partner</th>
          <th>Inkomsten</th>
          <th>Uitgaven</th>
          <th>Netto besteedbaar</th>
          <th>Gewenst netto besteedbaar</th>
          <th>Tekort/overschot</th>
          <th>Bijzonderheid</th>
        </tr>
      </thead>
      <tbody>
        {nbi.map((nbiI, index) => {
          // Bereken alle geformatteerde waarden eenmalig
          const inkomenFormatted = bedragFormatNull(!!nbiI.inkomenBedrag ? nbiI.inkomenBedrag / 12 : 0);
          const uitgavenFormatted = bedragFormatNull(!!nbiI.uitgavenBedrag ? nbiI.uitgavenBedrag / 12 : 0);
          const nbiFormatted = bedragFormatNull(!!nbiI.nbiBedrag ? nbiI.nbiBedrag / 12 : 0);
          const gewenstFormatted = bedragFormatNull(!!nbiI.gewenstNbiBedrag ? nbiI.gewenstNbiBedrag / 12 : 0);
          const tekortFormatted = bedragFormatNull(!!nbiI.tekortOverschotBedrag ? nbiI.tekortOverschotBedrag / 12 : 0);

          // Filter logica binnen de map functie
          if (index > 0) {
            const prev = nbi[index - 1];

            // Vergelijk met vorige rij geformatteerde waarden
            const prevGewenstFormatted = bedragFormatNull(!!prev.gewenstNbiBedrag ? prev.gewenstNbiBedrag / 12 : 0);
            const prevInkomenFormatted = bedragFormatNull(!!prev.inkomenBedrag ? prev.inkomenBedrag / 12 : 0);
            const prevBijzonderheden = prev.bijzonderheden;
            const prevTekortFormatted = bedragFormatNull(
              !!prev.tekortOverschotBedrag ? prev.tekortOverschotBedrag / 12 : 0
            );

            // Skip deze rij als de geformatteerde waarden gelijk zijn aan de vorige rij
            if (
              gewenstFormatted === prevGewenstFormatted &&
              tekortFormatted === prevTekortFormatted &&
              (nbiI.bijzonderheden?.filter(
                b => b.bijzonderheid !== "" && !b.bijzonderheid?.startsWith("Wijziging belastbaar inkomen")
              )?.length ?? 0) < 1
            ) {
              return null;
            }

            // Helper functie om te checken of bijzonderheden alleen "Wijziging belastbaar inkomen" bevatten
            const onlyContainsWijzigingBelastbaarInkomen = (bijzonderheden: any[] | null | undefined): boolean => {
              if (!bijzonderheden || bijzonderheden.length === 0) return false;
              return (
                bijzonderheden.filter(b => b.bijzonderheid || "").length === 1 &&
                bijzonderheden[0].bijzonderheid?.startsWith("Wijziging belastbaar inkomen")
              );
            };

            // Skip deze rij als inkomen gelijk is en beide alleen "Wijziging belastbaar inkomen" bevatten
            if (
              inkomenFormatted === prevInkomenFormatted &&
              onlyContainsWijzigingBelastbaarInkomen(nbiI.bijzonderheden) &&
              onlyContainsWijzigingBelastbaarInkomen(prevBijzonderheden)
            ) {
              return null;
            }
          }

          return (
            <tr key={index}>
              <td>{calculateYear(startDatum, nbiI.jaar)}</td>
              <td className="text-center">{nbiI.leeftijd1}</td>
              <td className="text-center">{nbiI.leeftijd2}</td>
              <td className="text-right">{inkomenFormatted}</td>
              <td className="text-right">{uitgavenFormatted}</td>
              <td className="text-right">{nbiFormatted}</td>
              <td className="text-right">{gewenstFormatted}</td>
              <td className="text-right">{tekortFormatted}</td>
              <td>{(nbiI.bijzonderheden?.map(c => c.bijzonderheid) || [""]).join(", ")}</td>
            </tr>
          );
        })}
      </tbody>
      {voetnoot && (
        <tfoot>
          <tr>
            <td colSpan={9} className="text-left font-italic">
              {voetnoot}
            </td>
          </tr>
        </tfoot>
      )}
    </Table>
  );
};
